function initAccessibilityControls(root = document) {
  // Scope everything to the nearest .a11y-control so multiple headers won't collide
  const controls = Array.from(root.querySelectorAll('.a11y-control'));
  if (!controls.length) return;

  // ===== ADD: helpers to scale elements that have fixed px sizes / high specificity =====
  function a11yBaselineTargets(ctx = document) {
    const sel = [
      'h1','h2','h3','h4','h5','h6',        // all headings
      '.feature-header',                     // your specific class (h1.feature-header…)
      '.nav.main-nav a', '#page-menu a',     // drawer + desktop nav links
      '.site-control__inner a', '.site-control__inner button'
    ].join(',');

    const nodes = Array.from(ctx.querySelectorAll(sel));
    nodes.forEach(el => {
      const cs = getComputedStyle(el).fontSize;
      const px = parseFloat(cs);
      if (!el.dataset.a11yBasePx || Math.abs(parseFloat(el.dataset.a11yBasePx) - px) > 0.5) {
        el.dataset.a11yBasePx = String(px); // store the computed baseline
      }
    });
  }

  function a11yApplyScaledTargets(getScaleFn, ctx = document) {
    const nodes = ctx.querySelectorAll('[data-a11y-base-px]');
    const s = getScaleFn();
    nodes.forEach(el => {
      const base = parseFloat(el.dataset.a11yBasePx || '0');
      if (base > 0) el.style.fontSize = (base * s) + 'px';
    });
  }
  // ===== /ADD =====

  controls.forEach((wrap) => {
    const toggle = wrap.querySelector('.accessibility-toggle');
    const menu   = wrap.querySelector('.accessibility-menu');
    if (!toggle || !menu) return;

    // Prevent double-binding (e.g., when section reloads)
    if (menu.dataset.a11yInit === 'true') return;
    menu.dataset.a11yInit = 'true';

    // Ensure ARIA
    if (!toggle.hasAttribute('aria-expanded')) toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', 'accessibility-menu');

    // ---- Persistence helpers ----
    const LS_KEY_SCALE = 'a11y:textScale';
    const LS_KEY_HC    = 'a11y:highContrast';

    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
    const getScale = () => parseFloat(localStorage.getItem(LS_KEY_SCALE) || '1');
    const setScale = (val) => {
      // CHANGE: safer lower bound so text never gets too small
      const v = clamp(val, 1.0, 1.5);
      document.documentElement.style.setProperty('--a11y-text-scale', v.toString());
      localStorage.setItem(LS_KEY_SCALE, v.toString());

      // ADD: re-apply scaling to px-locked elements whenever scale changes
      a11yApplyScaledTargets(getScale, document);
    };
    const applyContrast = (on) => {
      document.documentElement.classList.toggle('high-contrast', !!on);
      localStorage.setItem(LS_KEY_HC, on ? '1' : '0');
    };

    // Apply persisted prefs on load
    setScale(getScale());
    applyContrast(localStorage.getItem(LS_KEY_HC) === '1');

    // ADD: capture baselines and apply scaling for fixed-size targets
    a11yBaselineTargets(document);
    a11yApplyScaledTargets(getScale, document);

    // ---- Open/close menu ----
    const openMenu = () => {
      toggle.setAttribute('aria-expanded', 'true');
      menu.removeAttribute('hidden');
      // focus first action
      const first = menu.querySelector('button, [href], [tabindex]:not([tabindex="-1"])');
      if (first) first.focus();
      // close on outside click
      document.addEventListener('click', outsideHandler, { capture: true });
      document.addEventListener('keydown', escHandler);
    };

    const closeMenu = () => {
      toggle.setAttribute('aria-expanded', 'false');
      menu.setAttribute('hidden', '');
      document.removeEventListener('click', outsideHandler, { capture: true });
      document.removeEventListener('keydown', escHandler);
      // return focus
      toggle.focus();
    };

    const outsideHandler = (e) => {
      if (!wrap.contains(e.target)) closeMenu();
    };
    const escHandler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
      }
    };

    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      expanded ? closeMenu() : openMenu();
    });

    // ---- Actions ----
    menu.querySelectorAll('button[data-action]').forEach((btn) => {
      btn.addEventListener('click', () => {
        switch (btn.dataset.action) {
          case 'increase-text':
            setScale(getScale() + 0.1);
            break;
          case 'decrease-text':
            setScale(getScale() - 0.1);
            break;
          case 'reset-text':
            setScale(1);
            break;
          case 'high-contrast':
            applyContrast(!document.documentElement.classList.contains('high-contrast'));
            break;
        }
      });
    });
  });

  // ===== ADD: re-baseline on resize (handles breakpoint-specific font sizes) =====
  let a11yResizeTO;
  window.addEventListener('resize', () => {
    clearTimeout(a11yResizeTO);
    a11yResizeTO = setTimeout(() => {
      a11yBaselineTargets(document);
      // re-apply with current scale
      const current = parseFloat(localStorage.getItem('a11y:textScale') || '1');
      document.documentElement.style.setProperty('--a11y-text-scale', String(current));
      a11yApplyScaledTargets(() => current, document);
    }, 150);
  });
  // ===== /ADD =====
}

// Init on first load
document.addEventListener('DOMContentLoaded', () => initAccessibilityControls(document));
// Re-init if the header section is hot-reloaded in the Theme Editor
document.addEventListener('shopify:section:load', (e) => {
  initAccessibilityControls(e.target);

  // ADD: when a header/nav section reloads, baseline & apply for those nodes too
  // (important for Theme Editor or dynamic sections)
  const current = parseFloat(localStorage.getItem('a11y:textScale') || '1');
  const ctx = e.target || document;
  // Re-calc baselines for new markup and re-apply scale
  (function rebaseline(ctxNode){
    const sel = [
      'h1','h2','h3','h4','h5','h6','.feature-header',
      '.nav.main-nav a','#page-menu a','.site-control__inner a','.site-control__inner button'
    ].join(',');
    const nodes = Array.from(ctxNode.querySelectorAll(sel));
    nodes.forEach(el => {
      const px = parseFloat(getComputedStyle(el).fontSize);
      el.dataset.a11yBasePx = String(px);
      el.style.fontSize = (px * current) + 'px';
    });
  })(ctx);
});
